'use client';

import { useTranslations } from 'next-intl';
import { useHotPairs } from '@/lib/api/futures/hooks';
import { TRADE_PAIR_LIST_HREF, TRADE_ROUTE } from '@/lib/constants/routing';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { CardFooter } from './CardFooter';
import { CardHeader } from './CardHeader';
import { HOT_FUTURES_COUNT, HOT_FUTURES_MEMBER_COUNT, SIDEBAR_CARD_CLASS } from './constants';
import { HotFuturesRow, HotFuturesRowSkeleton } from './HotFuturesRow';
import { SidebarLink } from './SidebarLink';
import { useVisitor } from './useVisitor';

const SKELETON_ROWS = Array.from({ length: HOT_FUTURES_MEMBER_COUNT }, (_, i) => i);

interface HotFuturesCardProps {
  /** The request carried a session cookie. */
  hasSession: boolean;
}

/**
 * Hot Futures (Figma 15003:34208 / 15001:34649): the trending pairs, filled to
 * three, with prices streamed live. Guests also get the exchange pitch;
 * signed-in players get a fourth pair in its place.
 */
export function HotFuturesCard({ hasSession }: HotFuturesCardProps) {
  const t = useTranslations('home.hotFutures');
  const tNav = useTranslations('nav');
  const { isSignedIn, isGuest } = useVisitor(hasSession);
  const count = isSignedIn ? HOT_FUTURES_MEMBER_COUNT : HOT_FUTURES_COUNT;
  const { data: hotPairs, isLoading } = useHotPairs(count);

  const leverage = Math.max(0, ...hotPairs.map((pair) => pair.maxLeverage ?? 0));

  if (!isLoading && hotPairs.length === 0) return null;

  return (
    <section aria-label={t('title')} className={cn(SIDEBAR_CARD_CLASS, 'gap-4')}>
      <CardHeader title={t('title')} href={TRADE_PAIR_LIST_HREF} linkLabel={t('allPairs')} />
      <ul className="flex flex-col">
        {isLoading
          ? SKELETON_ROWS.slice(0, count).map((row) => (
              <li key={row}>
                <HotFuturesRowSkeleton />
              </li>
            ))
          : hotPairs.map((pair) => (
              <li key={pair.id}>
                <HotFuturesRow pair={pair} />
              </li>
            ))}
      </ul>
      {isGuest ? (
        <CardFooter
          text={t('pitch', { leverage })}
          action={
            <Button asChild appearance="accent" size="S" className="min-w-20 shrink-0">
              <SidebarLink href={TRADE_ROUTE}>{tNav('trade')}</SidebarLink>
            </Button>
          }
        />
      ) : null}
    </section>
  );
}
